/**
 * PATH: packages/email-hub/src/feature/components/RecipientAutocomplete.tsx
 * FILE_ID: EMAILHUB-FEATURE-COMPONENT-RECIPIENTAUTOCOMPLETE-001
 * PURPOSE: Canonical body of this email component. Lifted from
 *          apps/<tenant>/src/features/email/components/RecipientAutocomplete.tsx
 *          on K3a (see docs/architecture/EMAIL_KANBAN_CUTOVER_ARC.md).
 *          Tenants import this via thin re-export shims; edit
 *          THIS file, not the tenant copies.
 * WHY:     Removes operator-visible drift between tenants —
 *          previously every email feature change had to be
 *          mirrored manually in two tenant trees, and concurrent
 *          agents kept letting one side go stale (BUG-0032 was
 *          the most recent example). One canonical body, both
 *          tenants consume from this package.
 * RELATED:
 *   - apps/<tenant>/src/features/email/components/RecipientAutocomplete.tsx:
 *     thin re-export shim that points back here.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import api from '../../_internal/hooks/useApi'
import { emailApiFetch } from '../lib/api'
import { reportClientIssue } from '../../_internal/services/integrationEvents'
import { isValidEmail } from '../lib/emailAddress'
import { RecipientChip } from './RecipientChip'
import { RecipientSuggestions } from './RecipientSuggestions'

export type Suggestion = {
  name: string
  email: string
  source: 'outlook' | 'history' | 'org' | 'client' | 'contact' | string
  client_name?: string
  /** Ranking signals from the BE (recipient ranking MR). Optional so older
   *  sources / tenant FastAPI results still type-check. */
  org?: boolean
  last_seen?: string | null
  frequency?: number
}

type Props = {
  value: string[]
  onChange: (next: string[]) => void
  placeholder?: string
  ariaLabel?: string
  /** Focus the input on mount (e.g. the To field of a fresh New
   *  Message window). */
  autoFocus?: boolean
}

const MIN_QUERY_LEN = 2
const DEBOUNCE_MS = 180

type FetchOutcome = { ok: true; results: Suggestion[] } | { ok: false; reason: 'network' | 'auth' | 'server' }

async function fetchQuotechContacts(query: string, signal: AbortSignal): Promise<FetchOutcome> {
  try {
    const res = await emailApiFetch(
      `/api/emails/contacts/search/?q=${encodeURIComponent(query)}&limit=10`,
      { method: 'GET', signal } as RequestInit,
    )
    if (res.status === 401 || res.status === 403) {
      void reportClientIssue({
        source: 'client.email.contacts_search',
        severity: 'warning',
        reason: 'auth',
        message: `Quotech contacts search returned ${res.status}`,
        context: { query_length: query.length, side: 'quotech', http_status: res.status },
      })
      return { ok: false, reason: 'auth' }
    }
    if (!res.ok) {
      void reportClientIssue({
        source: 'client.email.contacts_search',
        severity: 'warning',
        reason: 'server',
        message: `Quotech contacts search returned HTTP ${res.status}`,
        context: { query_length: query.length, side: 'quotech', http_status: res.status },
      })
      return { ok: false, reason: 'server' }
    }
    const data = await res.json().catch(() => ({}))
    return { ok: true, results: Array.isArray(data?.results) ? data.results : [] }
  } catch (err) {
    if ((err as { name?: string } | null)?.name === 'AbortError') return { ok: true, results: [] }
    void reportClientIssue({
      source: 'client.email.contacts_search',
      severity: 'warning',
      reason: 'network',
      message: `Quotech contacts search network error: ${(err as Error)?.message || 'unknown'}`,
      context: { query_length: query.length, side: 'quotech' },
      detail: (err as Error)?.stack,
    })
    return { ok: false, reason: 'network' }
  }
}

/**
 * `/contacts/search` only exists on the tenant FastAPI backends
 * (SAM / Newmark / Steel). The dashboard.quotech.ai Django backend
 * serves contacts at `/api/emails/contacts/search/` (the "quotech"
 * source above) and 404s this path. Mirror the integration-events
 * module-scope 404 latch: probe once, then skip the local source for
 * the rest of the session instead of re-404ing on EVERY keystroke and
 * spamming the beacon with "server" issues for an endpoint that is
 * legitimately absent on this host.
 */
let localContactsEndpointMissing = false
let warnedAboutMissingLocalContacts = false

/** Test seam — module-scope latches survive between vitest cases. */
export function __resetLocalContactsLatchForTests(): void {
  localContactsEndpointMissing = false
  warnedAboutMissingLocalContacts = false
}

export async function fetchSteelContacts(query: string, signal: AbortSignal): Promise<FetchOutcome> {
  if (localContactsEndpointMissing) return { ok: true, results: [] }
  try {
    const res = await api.get('/contacts/search', {
      params: { q: query, limit: 10 },
      signal: signal as unknown as AbortSignal,
    })
    const results = (res.data as { results?: Suggestion[] } | undefined)?.results
    return { ok: true, results: Array.isArray(results) ? results : [] }
  } catch (err) {
    const status = (err as { response?: { status?: number } } | null)?.response?.status
    if (status === 404) {
      localContactsEndpointMissing = true
      if (!warnedAboutMissingLocalContacts) {
        warnedAboutMissingLocalContacts = true
        try {
          // eslint-disable-next-line no-console
          console.warn(
            '[email-hub] /api/contacts/search not available on this backend; ' +
              'local contacts source disabled for this session (Quotech contacts still active).',
          )
        } catch {
          // ignore logging failures
        }
      }
      // Endpoint absence is not an error state for the operator —
      // the Quotech source still serves suggestions.
      return { ok: true, results: [] }
    }
    if (status === 401 || status === 403) {
      void reportClientIssue({
        source: 'client.email.contacts_search',
        severity: 'warning',
        reason: 'auth',
        message: `Local contacts search returned ${status}`,
        context: { query_length: query.length, side: 'local', http_status: status },
      })
      return { ok: false, reason: 'auth' }
    }
    if ((err as { code?: string; name?: string } | null)?.name === 'CanceledError') return { ok: true, results: [] }
    void reportClientIssue({
      source: 'client.email.contacts_search',
      severity: 'warning',
      reason: status ? 'server' : 'network',
      message: status
        ? `Local contacts search returned HTTP ${status}`
        : `Local contacts search network error: ${(err as Error)?.message || 'unknown'}`,
      context: { query_length: query.length, side: 'local', http_status: status ?? null },
      detail: (err as Error)?.stack,
    })
    return { ok: false, reason: status ? 'server' : 'network' }
  }
}

/**
 * Merged contact lookup across both sources (Quotech Django + tenant
 * FastAPI), de-duplicated by address. Shared so the compose body's
 * @mention picker queries exactly the same people the To/Cc fields do —
 * one source of truth, no drift. Returns at most 8 to keep the inline
 * popover compact; errors degrade to whatever source succeeded.
 */
export async function searchEmailContacts(
  query: string, signal: AbortSignal,
): Promise<Suggestion[]> {
  const [quotech, steel] = await Promise.all([
    fetchQuotechContacts(query, signal),
    fetchSteelContacts(query, signal),
  ])
  if (signal.aborted) return []
  const qr = quotech.ok ? quotech.results : []
  const sr = steel.ok ? steel.results : []
  return rankSuggestions(dedupe([qr, sr])).slice(0, 8)
}

/** Merge contact lists by lowercased email. On a collision we KEEP the first
 *  occurrence but absorb ranking signals from later copies (org flag, the more
 *  recent last_seen, the larger frequency) so a duplicate across the two
 *  sources never drops the richer metadata one of them carried. */
function dedupe(lists: Suggestion[][]): Suggestion[] {
  const byKey = new Map<string, Suggestion>()
  const order: string[] = []
  for (const list of lists) {
    for (const entry of list) {
      const email = (entry?.email || '').trim()
      if (!email) continue
      const key = email.toLowerCase()
      const existing = byKey.get(key)
      if (!existing) {
        byKey.set(key, { ...entry, email })
        order.push(key)
        continue
      }
      byKey.set(key, {
        ...existing,
        org: existing.org || entry.org,
        source: existing.org || entry.org ? 'org' : existing.source,
        frequency: Math.max(existing.frequency ?? 0, entry.frequency ?? 0) || undefined,
        last_seen: pickNewer(existing.last_seen, entry.last_seen),
        client_name: existing.client_name || entry.client_name,
      })
    }
  }
  return order.map((k) => byKey.get(k)!)
}

function pickNewer(a?: string | null, b?: string | null): string | null | undefined {
  if (!a) return b
  if (!b) return a
  return new Date(a).getTime() >= new Date(b).getTime() ? a : b
}

function recencyPoints(lastSeen?: string | null): number {
  if (!lastSeen) return 0
  const t = new Date(lastSeen).getTime()
  if (!Number.isFinite(t)) return 0
  const days = (Date.now() - t) / 86_400_000
  if (days < 7) return 300
  if (days < 30) return 200
  if (days < 90) return 120
  if (days < 365) return 50
  return 10
}

/** Client-side re-rank of the MERGED two-source list (each source is ranked
 *  server-side, but the union needs one consistent order). Mirrors the BE
 *  scorer: org-internal colleagues first, then recent/frequent, with Outlook
 *  relevance as a prior. Pure + stable (equal scores keep merge order). */
export function rankSuggestions(list: Suggestion[]): Suggestion[] {
  const score = (s: Suggestion): number => {
    let total = 0
    if (s.org) total += 1000
    total += recencyPoints(s.last_seen)
    total += Math.min(s.frequency ?? 0, 50) * 2
    if (s.source === 'outlook') total += 60
    return total
  }
  return list
    .map((s, i) => ({ s, i }))
    .sort((a, b) => score(b.s) - score(a.s) || a.i - b.i)
    .map((x) => x.s)
}

export default function RecipientAutocomplete({
  value,
  onChange,
  placeholder,
  ariaLabel,
  autoFocus,
}: Props) {
  const [input, setInput] = useState('')
  const [suggestions, setSuggestions] = useState<Suggestion[]>([])
  const [open, setOpen] = useState(false)
  const [highlight, setHighlight] = useState(0)
  const [loading, setLoading] = useState(false)
  const [sourceErrors, setSourceErrors] = useState<{ outlook: boolean; local: boolean }>({ outlook: false, local: false })

  const inputRef = useRef<HTMLInputElement>(null)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const abortRef = useRef<AbortController | null>(null)
  // True once the operator explicitly arrowed through the dropdown —
  // only then does Enter pick the highlighted suggestion over a fully-
  // typed valid address. Reset whenever the input text changes.
  const userNavigatedRef = useRef(false)

  useEffect(() => {
    if (autoFocus) inputRef.current?.focus()
    // Mount-only by design — refocusing on later prop flips would
    // steal the caret from whatever the operator moved on to.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const selectedSet = useMemo(() => new Set(value.map(v => v.toLowerCase())), [value])

  const addRecipient = useCallback((email: string) => {
    const trimmed = email.trim()
    if (!trimmed) return
    if (selectedSet.has(trimmed.toLowerCase())) {
      setInput('')
      return
    }
    onChange([...value, trimmed])
    setInput('')
    setSuggestions([])
    setHighlight(0)
  }, [onChange, selectedSet, value])

  const removeRecipient = useCallback((email: string) => {
    onChange(value.filter(v => v.toLowerCase() !== email.toLowerCase()))
  }, [onChange, value])

  // Debounced search
  useEffect(() => {
    const query = input.trim()
    if (query.length < MIN_QUERY_LEN) {
      setSuggestions([])
      setLoading(false)
      abortRef.current?.abort()
      return
    }

    const timer = window.setTimeout(async () => {
      abortRef.current?.abort()
      const ctrl = new AbortController()
      abortRef.current = ctrl
      setLoading(true)
      const [quotech, steel] = await Promise.all([
        fetchQuotechContacts(query, ctrl.signal),
        fetchSteelContacts(query, ctrl.signal),
      ])
      if (ctrl.signal.aborted) return
      const quotechResults = quotech.ok ? quotech.results : []
      const steelResults = steel.ok ? steel.results : []
      const merged = rankSuggestions(
        dedupe([quotechResults, steelResults]).filter(s => !selectedSet.has(s.email.toLowerCase())),
      )
      setSuggestions(merged.slice(0, 10))
      setHighlight(0)
      setSourceErrors({ outlook: !quotech.ok, local: !steel.ok })
      // Keep the dropdown open so the inline error footer is visible even if
      // we got zero results. Otherwise the user would see nothing and assume
      // the feature is broken rather than understanding *why*.
      const hasErrors = !quotech.ok || !steel.ok
      setOpen(merged.length > 0 || hasErrors)
      setLoading(false)
    }, DEBOUNCE_MS)

    return () => window.clearTimeout(timer)
  }, [input, selectedSet])

  // Close dropdown on outside click
  useEffect(() => {
    function onDoc(e: MouseEvent) {
      if (!wrapperRef.current?.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [])

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if ((e.key === ',' || e.key === ';' || e.key === 'Tab' || e.key === 'Enter') && input.trim()) {
      // BUG-2026-06-11-RECIPIENT-ENTER-SWAP: a fully-typed valid
      // address used to lose to suggestions[0] (auto-highlighted by
      // the async search), silently swapping bob@x.com for a similar
      // contact. The typed address now wins unless the operator
      // explicitly arrowed onto a suggestion.
      const chosen = open && suggestions[highlight]
      if (isValidEmail(input) && !userNavigatedRef.current) {
        e.preventDefault()
        addRecipient(input)
        return
      }
      if (chosen) {
        e.preventDefault()
        addRecipient(chosen.email)
        return
      }
      if (isValidEmail(input)) {
        e.preventDefault()
        addRecipient(input)
        return
      }
      // Non-email plain text + Enter: swallow only for Enter so form doesn't submit
      if (e.key === 'Enter') e.preventDefault()
      return
    }
    if (e.key === 'Backspace' && !input && value.length > 0) {
      onChange(value.slice(0, -1))
      return
    }
    if (!open || suggestions.length === 0) return
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      userNavigatedRef.current = true
      setHighlight(h => (h + 1) % suggestions.length)
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      userNavigatedRef.current = true
      setHighlight(h => (h - 1 + suggestions.length) % suggestions.length)
    } else if (e.key === 'Escape') {
      setOpen(false)
    }
  }

  // Commit a fully-typed valid address when focus leaves the field —
  // operators type an address then click straight on Send/Subject; the
  // uncommitted text used to silently vanish ("no recipient" or, worse,
  // an email that went out without the person they typed). Clicking a
  // SUGGESTION is safe: its onMouseDown preventDefault()s so no blur
  // fires.
  const handleBlur = () => {
    if (isValidEmail(input)) addRecipient(input)
  }

  // Pasting "a@x.com, b@y.com; c@z.com" used to land as ONE chip-less
  // string. Split on the usual separators, chip every valid address,
  // keep any non-address remainder in the input for manual fixing.
  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const text = e.clipboardData.getData('text')
    if (!text || !/[,;\s]/.test(text.trim())) return
    e.preventDefault()
    const parts = text.split(/[,;\s]+/).map(p => p.trim()).filter(Boolean)
    const valid = parts.filter(isValidEmail)
    const invalid = parts.filter(p => !isValidEmail(p))
    if (valid.length > 0) {
      const seen = new Set(value.map(v => v.toLowerCase()))
      const fresh = valid.filter(v => {
        const k = v.toLowerCase()
        if (seen.has(k)) return false
        seen.add(k)
        return true
      })
      if (fresh.length > 0) onChange([...value, ...fresh])
    }
    setInput(invalid.join(' '))
  }

  return (
    <div ref={wrapperRef} className="relative flex-1">
      <div
        className="flex flex-wrap gap-1.5 items-center min-h-[38px] px-2 py-1 border-b border-border focus-within:border-primary"
        onClick={() => inputRef.current?.focus()}
      >
        {value.map((email) => (
          <RecipientChip key={email} email={email} onRemove={removeRecipient} />
        ))}
        <input
          ref={inputRef}
          type="text"
          value={input}
          onChange={(e) => { userNavigatedRef.current = false; setInput(e.target.value) }}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          onPaste={handlePaste}
          onFocus={() => { if (suggestions.length > 0) setOpen(true) }}
          placeholder={value.length === 0 ? (placeholder ?? 'recipient@email.com') : ''}
          aria-label={ariaLabel ?? 'Recipients'}
          aria-autocomplete="list"
          aria-expanded={open}
          className="flex-1 min-w-[160px] bg-transparent focus:outline-none text-foreground py-1"
        />
      </div>

      {open && (suggestions.length > 0 || sourceErrors.outlook || sourceErrors.local) && (
        <RecipientSuggestions
          suggestions={suggestions}
          highlight={highlight}
          loading={loading}
          sourceErrors={sourceErrors}
          onHighlight={setHighlight}
          onPick={addRecipient}
        />
      )}
    </div>
  )
}
